/**
 * Utilities for displaying custom field values in the UI.
 * 
 * Custom fields have various value types (Integer, Decimal, Boolean, String, etc.)
 * and need to be formatted appropriately for display on ad cards and detail modals.
 */

import type { CustomFieldInfo, CustomFieldValue } from '@/types/classifiedOrder';
import { CustomFieldValueType } from '@/types/classifiedOrder';

/**
 * Represents a formatted custom field for display.
 */
export interface DisplayCustomField {
  key: number;
  name: string;
  displayValue: string;
  fullValue: string; // Untruncated value for modal display
}

/**
 * Get the raw value from a CustomFieldValue based on its type.
 */
export function getCustomFieldRawValue(value: CustomFieldValue): string | number | boolean | null {
  switch (value.valueType) {
    case CustomFieldValueType.Integer:
      return value.integerValue ?? null;
    case CustomFieldValueType.Decimal:
      return value.decimalValue ?? null;
    case CustomFieldValueType.Boolean:
      return value.booleanValue ?? null;
    case CustomFieldValueType.String:
      return value.stringValue ?? null;
    case CustomFieldValueType.Memo:
      return value.memoValue ?? null;
    case CustomFieldValueType.FileName:
      return value.fileNameValue ?? null;
    case CustomFieldValueType.ListOfAllowedValues:
      return value.listOfAllowedValuesValue ?? null;
    default:
      return null;
  }
}

/**
 * Format a number with thousands separator.
 */
function formatWithThousands(value: number, decimals = 0): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Check if a field name indicates a price/currency field (case insensitive).
 */
function isPriceField(fieldName?: string): boolean {
  if (!fieldName) return false;
  const lower = fieldName.toLowerCase();
  return lower === 'price' || lower.includes('price');
}

/**
 * Get the display value from a CustomFieldValue based on its type and field info.
 * Handles formatting for numbers, booleans, and truncation for long text.
 * 
 * @param value - The custom field value
 * @param fieldInfo - The custom field definition (optional, for formatting preferences)
 * @param maxLength - Maximum length for string values (0 = no limit)
 */
export function formatCustomFieldValue(
  value: CustomFieldValue,
  fieldInfo?: CustomFieldInfo,
  maxLength = 0
): string {
  const rawValue = getCustomFieldRawValue(value);
  
  if (rawValue === null || rawValue === undefined) {
    return '';
  }
  
  // Check if this is a price field for currency formatting
  const isPrice = isPriceField(fieldInfo?.name);
  const currencyPrefix = isPrice ? '$' : '';
  
  switch (value.valueType) {
    case CustomFieldValueType.Integer: {
      const numVal = rawValue as number;
      if (fieldInfo?.useThousandsSeparator) {
        return currencyPrefix + formatWithThousands(numVal, 0);
      }
      return currencyPrefix + String(numVal);
    }
    
    case CustomFieldValueType.Decimal: {
      const numVal = rawValue as number;
      const scale = fieldInfo?.scale ?? 2;
      
      // Check if the decimal portion is all zeros
      const hasDecimalPortion = numVal % 1 !== 0;
      const effectiveScale = hasDecimalPortion ? scale : 0;
      
      if (fieldInfo?.useThousandsSeparator) {
        return currencyPrefix + formatWithThousands(numVal, effectiveScale);
      }
      return currencyPrefix + (effectiveScale > 0 ? numVal.toFixed(effectiveScale) : String(Math.round(numVal)));
    }
    
    case CustomFieldValueType.Boolean:
      return rawValue ? 'Yes' : 'No';
    
    case CustomFieldValueType.String:
    case CustomFieldValueType.Memo: {
      let strVal = String(rawValue);
      if (maxLength > 0 && strVal.length > maxLength) {
        strVal = strVal.substring(0, maxLength - 3) + '...';
      }
      return strVal;
    }
    
    case CustomFieldValueType.FileName:
      return String(rawValue);
    
    case CustomFieldValueType.ListOfAllowedValues: {
      // For list values, the stored value might be the internal value
      // Try to find the display name from allowed values if available
      const strVal = String(rawValue);
      if (fieldInfo?.allowedValues) {
        const match = fieldInfo.allowedValues.find(av => av.value === strVal);
        if (match) {
          return match.name;
        }
      }
      return strVal;
    }
    
    default:
      return String(rawValue);
  }
}

/**
 * Check if a custom field value is empty/null.
 */
export function isCustomFieldValueEmpty(value: CustomFieldValue): boolean {
  const rawValue = getCustomFieldRawValue(value);
  if (rawValue === null || rawValue === undefined) {
    return true;
  }
  // Empty strings are considered empty
  if (typeof rawValue === 'string' && rawValue.trim() === '') {
    return true;
  }
  return false;
}

/**
 * Build a list of DisplayCustomField objects from custom field values and definitions.
 * Filters out empty values and sorts by relativeOrder.
 * 
 * @param values - Array of custom field values from the ad
 * @param fieldDefinitions - Array of custom field definitions for the classification
 * @param maxDisplayLength - Maximum length for display values (for chips)
 * @param limit - Maximum number of fields to return (0 = no limit)
 */
export function buildDisplayCustomFields(
  values: CustomFieldValue[],
  fieldDefinitions: CustomFieldInfo[],
  maxDisplayLength = 30,
  limit = 0
): DisplayCustomField[] {
  if (!values || values.length === 0) {
    return [];
  }
  
  // Create a map of field definitions by key for quick lookup
  const fieldMap = new Map<number, CustomFieldInfo>();
  for (const field of fieldDefinitions) {
    fieldMap.set(field.customFieldKey, field);
  }
  
  // Build display fields
  const displayFields: DisplayCustomField[] = [];
  
  for (const value of values) {
    // Skip empty values
    if (isCustomFieldValueEmpty(value)) {
      continue;
    }
    
    const fieldInfo = fieldMap.get(value.key);
    const name = fieldInfo?.name || `Field ${value.key}`;
    
    // Format for display (truncated for chips)
    const displayValue = formatCustomFieldValue(value, fieldInfo, maxDisplayLength);
    // Format for modal (full value)
    const fullValue = formatCustomFieldValue(value, fieldInfo, 0);
    
    if (displayValue) {
      displayFields.push({
        key: value.key,
        name,
        displayValue,
        fullValue,
      });
    }
  }
  
  // Sort by relativeOrder (lower = higher priority)
  displayFields.sort((a, b) => {
    const orderA = fieldMap.get(a.key)?.relativeOrder ?? 999;
    const orderB = fieldMap.get(b.key)?.relativeOrder ?? 999;
    return orderA - orderB;
  });
  
  // Apply limit if specified
  if (limit > 0 && displayFields.length > limit) {
    return displayFields.slice(0, limit);
  }
  
  return displayFields;
}
